import React, { Component } from 'react';
import http from '../../modules/api';

const CONTENT_REGISTRATION_PATH = /^\/shop\/instagram\/InstagramCreatePost(?:_?v\d+)?\/?$/i;

export const getCopywriterBackdropColor = () =>
  CONTENT_REGISTRATION_PATH.test(window.location.pathname) ? 'transparent' : 'rgba(10, 26, 47, .42)';

const getLanguageName = (code) => {
  const normalized = String(code || '').trim().toLowerCase();
  const languageMap = {
    ko: '한국어',
    'ko-kr': '한국어',
    en: '영어',
    'en-us': '영어',
    'en-gb': '영어',
    ja: '일본어',
    'ja-jp': '일본어',
    zh: '중국어',
    'zh-cn': '중국어(간체)',
    'zh-tw': '중국어(번체)',
    es: '스페인어',
    fr: '프랑스어',
    de: '독일어',
    vi: '베트남어',
    th: '태국어',
    hi: '힌디어',
    ar: '아랍어',
    bn: '벵골어',
    ru: '러시아어',
    pt: '포르투갈어',
  };

  return languageMap[normalized] || code || '-';
};

const formatDate = (value) => {
  if (!value) return '';

  const normalized = String(value).replace('T', ' ').trim();
  const matched = normalized.match(/^(\d{4})[-./](\d{1,2})[-./](\d{1,2})/);

  if (!matched) return normalized.slice(0, 10);

  return `${matched[1]}.${String(matched[2]).padStart(2, '0')}.${String(matched[3]).padStart(2, '0')}`;
};

const BASIC_AI_PRESET = {
  id: '',
  title: '베이직 AI',
  description: 'AI 카피라이터 설정 없이 기본 방식으로 인스타그램 문장을 생성합니다.',
  voice: '',
  learning: '',
  weight: '',
  languageCode: 'ko',
  language: '한국어',
  date: '',
  isBasic: true,
};

class AICopywriterModal extends Component {
  constructor(props) {
    super(props);

    this.modalRef = React.createRef();
    this.searchRef = React.createRef();
    this.dragContext = null;

    this.state = {
      searchQuery: '',
      selectedId: props.selectedId || '',
      presets: [],
      isLoading: false,
      isApplyingDefault: false,
      presetEditorRequest: null,
      PresetEditor: null,
      dragX: 0,
      dragY: 0,
    };
  }

  componentDidMount() {
    if (this.props.isOpen) {
      this.loadPresetList();
    }
    window.addEventListener('message', this.handleMessage);
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.isOpen && this.props.isOpen) {
      this.setState({
        searchQuery: '',
        selectedId: this.props.selectedId || '',
        isApplyingDefault: false,
        presetEditorRequest: null,
        dragX: 0,
        dragY: 0,
      }, this.loadPresetList);
      return;
    }

    if (prevProps.selectedId !== this.props.selectedId) {
      this.setState({ selectedId: this.props.selectedId || '' });
    }
  }

  componentWillUnmount() {
    this.presetListRequest = null;
    window.removeEventListener('message', this.handleMessage);
    this.stopModalDrag();
  }

  handleMessage = (event) => {
    if (event && event.data && event.data.type === 'copywriter_setting_saved') {
      this.loadPresetList();
    }
  };

  loadPresetList = () => {
    const request = {};
    this.presetListRequest = request;
    this.setState({ isLoading: true });

    http.get('/rest/shop/instagram/copywriter/preset/modal/list')
      .then((response) => {
        if (this.presetListRequest !== request) return;
        if (!response || !response.data || response.data.status !== 'success') {
          this.setState({
            presets: [{ ...BASIC_AI_PRESET, defaultYn: 'Y' }],
            isLoading: false,
          });
          return;
        }

        const savedPresetList = (response.data.list || []).map((item) => {
          const voiceParts = [];

          if (item.use_basic_voice == 1 || item.use_basic_voice === true || item.use_basic_voice === 'Y') {
            voiceParts.push('AI 브랜드 보이스');
          }
          if (item.use_myfeed_voice == 1 || item.use_myfeed_voice === true || item.use_myfeed_voice === 'Y') {
            voiceParts.push('내 기존 피드');
          }
          if (item.use_custom_voice == 1 || item.use_custom_voice === true || item.use_custom_voice === 'Y') {
            voiceParts.push('커스텀 뱅크');
          }

          let learning = '-';
          if (item.learning_data_type === 'multi') {
            learning = '멀티모달 매핑 (사진 + 텍스트)';
          } else if (item.learning_data_type === 'text') {
            learning = '텍스트 학습';
          } else if (item.learning_data_type === 'image') {
            learning = '이미지 학습';
          }

          const languageCode =
            item.language_code ||
            item.language ||
            item.language_cd ||
            item.preset_language ||
            item.generation_language ||
            'ko';

          return {
            id: String(item.preset_seq),
            title: item.preset_title || '제목 없는 AI 카피라이터',
            description:
              item.preset_description ||
              item.description ||
              item.memo ||
              item.reference_summary ||
              '저장된 브랜드 톤과 작성 규칙을 적용합니다.',
            voice: voiceParts.length > 0 ? voiceParts.join(' + ') : '선택 없음',
            learning,
            weight: learning,
            languageCode,
            language: getLanguageName(languageCode),
            date: formatDate(
              item.mod_date ||
              item.updated_at ||
              item.reg_date ||
              item.created_at ||
              item.create_date
            ),
            defaultYn: item.default_yn || 'N',
          };
        });

        const hasSavedDefault = savedPresetList.some((preset) => preset.defaultYn === 'Y');
        const basicPreset = {
          ...BASIC_AI_PRESET,
          defaultYn: hasSavedDefault ? 'N' : 'Y',
        };
        const parsedList = [basicPreset, ...savedPresetList];

        this.setState({
          presets: parsedList,
          isLoading: false,
        });

        if (this.props.getCopywriterList) {
          this.props.getCopywriterList(parsedList);
        }
      })
      .catch((error) => {
        if (this.presetListRequest !== request) return;
        console.error('AI 카피라이터 프리셋 목록 조회 실패:', error);
        this.setState({
          presets: [{ ...BASIC_AI_PRESET, defaultYn: 'Y' }],
          isLoading: false,
        });
      });
  };

  handleSelect = (preset) => {
    this.setState({
      selectedId: preset ? preset.id : '',
    });
  };

  handleApply = async () => {
    const { selectedId, presets, isApplyingDefault, presetEditorRequest } = this.state;
    if (isApplyingDefault || presetEditorRequest) return;

    const selectedPreset = presets.find(
      (preset) => String(preset.id) === String(selectedId)
    ) || null;

    if (!selectedPreset) return;

    this.setState({ isApplyingDefault: true });

    try {
      if (this.props.onApply) {
        const applied = await this.props.onApply(selectedId, selectedPreset);
        if (applied === false) return;
      }

      const nextPresets = presets.map((preset) => ({
        ...preset,
        defaultYn:
          String(preset.id || '') === String(selectedId || '') ? 'Y' : 'N',
      }));

      this.setState({ presets: nextPresets });

      if (this.props.getCopywriterList) {
        this.props.getCopywriterList(nextPresets);
      }

      this.handleClose();
    } finally {
      this.setState({ isApplyingDefault: false });
    }
  };

  openPresetEditor = async (presetId = null) => {
    if (this.state.isApplyingDefault || this.state.presetEditorRequest) return;

    const request = { presetId };
    this.setState({ presetEditorRequest: request });
    if (this.state.PresetEditor) return;

    try {
      // 설정 페이지에서도 카피라이터 모달을 사용하므로 편집 시점에 불러온다.
      const { default: PresetEditor } = await import('../InstagramCopywriterSetting_v2');
      if (this.props.isOpen && this.state.presetEditorRequest === request) {
        this.setState({ PresetEditor });
      }
    } catch (error) {
      if (!this.props.isOpen || this.state.presetEditorRequest !== request) return;
      console.error('AI 카피라이터 설정 화면 로딩 실패:', error);
      this.setState({ presetEditorRequest: null });
      window.alert('카피라이터 설정 화면을 불러오지 못했습니다. 다시 시도해주세요.');
    }
  };

  handleCancelPresetEditor = () => {
    this.setState({ presetEditorRequest: null }, () => this.searchRef.current?.focus());
  };

  handlePresetSaved = () => {
    this.setState({ presetEditorRequest: null, searchQuery: '' }, () => {
      this.loadPresetList();
      this.searchRef.current?.focus();
    });
  };

  handleClose = () => {
    this.stopModalDrag();

    this.setState({
      selectedId: this.props.selectedId || '',
      isApplyingDefault: false,
      presetEditorRequest: null,
      dragX: 0,
      dragY: 0,
    }, () => {
      if (this.props.onClose) {
        this.props.onClose();
      }
    });
  };

  startModalDrag = (e) => {
    if (e.button !== 0) return;
    if (e.target.closest('button, a, input, textarea, select, label')) return;

    const modal = this.modalRef.current;
    if (!modal) return;

    const rect = modal.getBoundingClientRect();
    this.dragContext = {
      startClientX: e.clientX,
      startClientY: e.clientY,
      startDragX: this.state.dragX || 0,
      startDragY: this.state.dragY || 0,
      startRect: rect,
    };

    document.addEventListener('mousemove', this.handleModalDrag);
    document.addEventListener('mouseup', this.stopModalDrag);
    e.preventDefault();
  };

  handleModalDrag = (e) => {
    const drag = this.dragContext;
    if (!drag) return;

    const rawDx = e.clientX - drag.startClientX;
    const rawDy = e.clientY - drag.startClientY;

    const viewportWidth =
      window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;

    const margin = 6;
    const visibleHeaderHeight = 40;

    // 모달 너비의 65%까지 좌우 화면 밖으로 이동 허용
    const horizontalOverflow = drag.startRect.width * 0.65;

    const minLeft = margin - horizontalOverflow;
    const maxLeft =
      viewportWidth -
      drag.startRect.width +
      horizontalOverflow -
      margin;

    const nextLeft = Math.min(
      maxLeft,
      Math.max(minLeft, drag.startRect.left + rawDx)
    );

    const minTop = margin;
    const maxTop = Math.max(
      margin,
      viewportHeight - visibleHeaderHeight
    );

    const nextTop = Math.min(
      maxTop,
      Math.max(minTop, drag.startRect.top + rawDy)
    );

    this.setState({
      dragX:
        drag.startDragX +
        (nextLeft - drag.startRect.left),
      dragY:
        drag.startDragY +
        (nextTop - drag.startRect.top),
    });
  };

  stopModalDrag = () => {
    document.removeEventListener('mousemove', this.handleModalDrag);
    document.removeEventListener('mouseup', this.stopModalDrag);
    this.dragContext = null;
  };

  renderPresetItem = (preset) => {
    const { selectedId, isApplyingDefault, presetEditorRequest } = this.state;
    const isSelected = String(selectedId || '') === String(preset.id || '');

    return (
      <div key={preset.id} className={`ai-copywriter-select-card${preset.isBasic ? '' : ' editable'}`}>
        <button
          type="button"
          className={`ai-copywriter-select-item ${isSelected ? 'selected' : ''}`}
          aria-pressed={isSelected}
          onClick={() => this.handleSelect(preset)}
        >
          <span className="ai-copywriter-select-radio" aria-hidden="true">
            {isSelected ? <span /> : null}
          </span>

          <span className="ai-copywriter-select-item-main">
            <span className="ai-copywriter-select-item-topline">
              <span className="ai-copywriter-select-item-type">{preset.isBasic ? '기본' : '설정제목'}</span>
              {preset.date ? <span className="ai-copywriter-select-item-date">{preset.date}</span> : null}
            </span>

            <strong className="ai-copywriter-select-item-title" title={preset.title}>
              {preset.title}
            </strong>

            {/* <span className="ai-copywriter-select-item-description">
              {preset.description}
            </span> */}

            {/* {!preset.isBasic && (
              <span className="ai-copywriter-select-item-meta">
                <span title={preset.voice}>보이스: {preset.voice}</span>
                <span title={preset.learning}>학습: {preset.learning}</span>
                <span>언어: {preset.language}</span>
              </span>
            )} */}
          </span>
        </button>
        {!preset.isBasic && (
          <button
            type="button"
            className="ai-copywriter-select-edit"
            aria-label={`${preset.title} 수정하기`}
            disabled={isApplyingDefault || !!presetEditorRequest}
            onClick={() => this.openPresetEditor(preset.id)}
          >
            상세보기 및 수정
          </button>
        )}
      </div>
    );
  };

  render() {
    if (!this.props.isOpen) return null;

    const {
      searchQuery,
      presets,
      isLoading,
      isApplyingDefault,
      selectedId,
      presetEditorRequest,
      PresetEditor,
      dragX,
      dragY,
    } = this.state;

    const normalizedQuery = String(searchQuery || '').trim().toLowerCase();
    const filteredPresets = presets.filter((preset) =>
      String(preset.title || '').toLowerCase().includes(normalizedQuery)
    );
    const canApply = !isApplyingDefault && !presetEditorRequest && presets.some(
      (preset) => String(preset.id) === String(selectedId)
    );

    return (
      <>
        <style>{`
          #AICopywriterSelectModal {
            --cw-mint: #00e2e6;
            --cw-mint-deep: #18bfc3;
            --cw-mint-bg: #eafafb;
            --cw-text: #202733;
            --cw-subtext: #7b8797;
            --cw-line: #dfe5eb;
            --cw-soft: #f7f9fb;
            font-family: inherit;
          }

          #AICopywriterSelectModal.ai-copywriter-select-overlay {
            position: fixed !important;
            inset: 0 !important;
            z-index: 13020 !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            padding: 16px !important;
            background: ${getCopywriterBackdropColor()} !important;
            pointer-events: none !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-modal {
            position: fixed !important;
            left: 50% !important;
            top: 50% !important;
            width: min(780px, calc(100vw - 24px)) !important;
            height: min(780px, calc(100vh - 24px)) !important;
            max-width: none !important;
            max-height: none !important;
            display: flex !important;
            flex-direction: column !important;
            overflow: hidden !important;
            margin: 0 !important;
            padding: 0 !important;
            // border: 1px solid #98a2ad !important;
            border-radius: 12px !important;
            background: #fff !important;
            box-shadow: 0 8px 24px rgba(20, 29, 43, .24) !important;
            pointer-events: auto !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-header {
            flex: 0 0 auto !important;
            display: flex !important;
            align-items: flex-start !important;
            justify-content: space-between !important;
            gap: 12px !important;
            height: auto !important;
            min-height: 96px !important;
            box-sizing: border-box !important;
            margin: 0 !important;
            padding: 26px 30px 10px !important;
            border: 0 !important;
            border-bottom: 1px solid #edf0f3 !important;
            border-radius: 0 !important;
            background: #fff !important;
            cursor: move !important;
            user-select: none !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-heading {
            min-width: 0;
            overflow-wrap: anywhere;
          }

          #AICopywriterSelectModal .ai-copywriter-select-title {
            margin: 0 !important;
            color: #202733 !important;
            font-size: 24px !important;
            font-weight: 800 !important;
            line-height: 1.25 !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-description {
            margin: 7px 0 0 !important;
            color: #8792a0 !important;
            font-size: 14px !important;
            font-weight: 400 !important;
            line-height: 1.45 !important;
            white-space: normal !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-close {
            width: 38px !important;
            height: 38px !important;
            flex: 0 0 38px !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            margin: -12px -10px 0 0 !important;
            padding: 0 0 0.1rem 0 !important;
            border: 0 !important;
            border-radius: 7px !important;
            background: transparent !important;
            color: #5f6873 !important;
            font-size: 30px !important;
            font-weight: 300 !important;
            line-height: 1 !important;
            cursor: pointer !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-close:hover {
            background: #f1f3f5 !important;
            color: #111827 !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-body {
            min-height: 0 !important;
            flex: 1 1 auto !important;
            display: flex !important;
            flex-direction: column !important;
            margin: 0 !important;
            padding: 22px 26px 18px !important;
            overflow: hidden !important;
            background: #fff !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-search-wrap {
            position: relative !important;
            flex: 0 0 auto !important;
            margin: 0 0 12px !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-search-icon {
            position: absolute !important;
            left: 13px !important;
            top: 50% !important;
            transform: translateY(-50%) !important;
            color: #a2aab4 !important;
            font-size: 14px !important;
            pointer-events: none !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-search {
            width: 100% !important;
            height: 50px !important;
            margin: 0 !important;
            padding: 0 14px 0 39px !important;
            border: 1px solid #d8dee5 !important;
            border-radius: 6px !important;
            background: #fff !important;
            color: #27303d !important;
            font-size: 14px !important;
            line-height: 50px !important;
            outline: none !important;
            box-shadow: none !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-search:focus {
            border-color: var(--cw-mint) !important;
            box-shadow: 0 0 0 2px rgba(69, 210, 212, .12) !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-list {
            min-height: 0 !important;
            flex: 1 1 auto !important;
            display: flex !important;
            flex-direction: column !important;
            gap: 14px !important;
            margin: 0 !important;
            padding: 0 3px 0 0 !important;
            overflow-x: hidden !important;
            overflow-y: auto !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-card {
            --cw-edit-button-height: 28px;
            --cw-edit-button-width: 112px;
            position: relative;
            flex-shrink: 0;
          }

          #AICopywriterSelectModal .ai-copywriter-select-item {
            width: 100% !important;
            // min-height: 122px !important;
            display: grid !important;
            grid-template-columns: 18px minmax(0, 1fr) !important;
            align-items: flex-start !important;
            gap: 14px !important;
            margin: 0 !important;
            padding: 13px 14px !important;
            border: 1px solid #dfe5eb !important;
            border-radius: 9px !important;
            background: #fff !important;
            color: inherit !important;
            text-align: left !important;
            box-shadow: none !important;
            cursor: pointer !important;
            transition: border-color .15s ease, background .15s ease !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-item:hover {
            border-color: #8ee3e5 !important;
            transform: none !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-item.selected {
            padding: 12px 13px 13px 10px !important;
            border: 2px solid var(--cw-mint) !important;
            // border-left-width: 4px !important;
            background: var(--cw-mint-bg) !important;
            box-shadow: none !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-radio {
            width: 16px !important;
            height: 16px !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            margin-top: 1px !important;
            border: 1px solid #c6ced7 !important;
            border-radius: 50% !important;
            background: #fff !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-item.selected .ai-copywriter-select-radio {
            border-color: var(--cw-mint) !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-radio > span {
            width: 8px !important;
            height: 8px !important;
            border-radius: 50% !important;
            background: var(--cw-mint) !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-item-main {
            min-width: 0 !important;
            display: block !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-item-topline {
            display: flex !important;
            align-items: center !important;
            justify-content: space-between !important;
            gap: 8px !important;
            min-width: 0 !important;
            margin: 0 0 2px !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-item-type,
          #AICopywriterSelectModal .ai-copywriter-select-item-date {
            color: #9aa4b0 !important;
            font-size: 10px !important;
            font-weight: 600 !important;
            line-height: 1.2 !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-item-title {
            display: block !important;
            max-width: 100% !important;
            margin: 0 0 4px !important;
            overflow: hidden !important;
            color: #202733 !important;
            font-size: 15px !important;
            font-weight: 800 !important;
            line-height: 1.3 !important;
            text-overflow: ellipsis !important;
            white-space: nowrap !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-card.editable .ai-copywriter-select-item-title {
            margin: 0 calc(var(--cw-edit-button-width) + 8px) 0 0 !important;
            line-height: var(--cw-edit-button-height) !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-item-description {
            display: block !important;
            max-width: 100% !important;
            margin: 0 0 8px !important;
            overflow: hidden !important;
            color: #6f7b89 !important;
            font-size: 11px !important;
            font-weight: 400 !important;
            line-height: 1.4 !important;
            text-overflow: ellipsis !important;
            white-space: nowrap !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-item-meta {
            display: flex !important;
            align-items: center;
            gap: 7px !important;
            min-width: 0 !important;
            min-height: 28px;
            margin-right: 76px;
          }

          #AICopywriterSelectModal .ai-copywriter-select-item-meta > span {
            min-width: 0 !important;
            display: block !important;
            margin: 0 !important;
            padding: 5px 7px !important;
            overflow: hidden !important;
            border: 0 !important;
            border-radius: 4px !important;
            background: #f1f5f9 !important;
            color: #475569 !important;
            font-size: 10px !important;
            font-weight: 500 !important;
            line-height: 1.3 !important;
            text-overflow: ellipsis !important;
            white-space: nowrap !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-item.selected .ai-copywriter-select-item-meta > span {
            background: rgba(255, 255, 255, .75) !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-edit {
            position: absolute;
            right: 15px;
            bottom: 14px;
            box-sizing: border-box;
            width: var(--cw-edit-button-width);
            height: var(--cw-edit-button-height);
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 0 8px;
            border: 1px solid #d8dee5;
            border-radius: 5px;
            background: #fff;
            color: #3f4a57;
            font-size: 12px;
            font-weight: 600;
            line-height: 1.2;
            white-space: nowrap;
            cursor: pointer;
          }

          #AICopywriterSelectModal .ai-copywriter-select-item.selected + .ai-copywriter-select-edit {
            bottom: 15px;
          }

          #AICopywriterSelectModal .ai-copywriter-select-edit:hover:not(:disabled) {
            border-color: var(--cw-mint);
          }

          #AICopywriterSelectModal .ai-copywriter-select-edit:disabled {
            opacity: .5;
            cursor: not-allowed;
          }

          #AICopywriterSelectModal .ai-copywriter-select-empty {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            min-height: 210px !important;
            padding: 28px !important;
            border: 1px dashed #cfd6de !important;
            border-radius: 8px !important;
            background: #fafbfc !important;
            color: #7d8793 !important;
            font-size: 14px !important;
            text-align: center !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-footer {
            flex: 0 0 auto !important;
            height: auto !important;
            display: flex !important;
            align-items: center !important;
            justify-content: space-between !important;
            gap: 14px !important;
            min-height: 80px !important;
            margin: 0 !important;
            padding: 12px 20px !important;
            border: 0 !important;
            border-top: 1px solid #edf0f3 !important;
            background: #fff !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-create {
            height: 46px !important;

            padding: 0 20px;
            border-radius: var(--radius-sm);
            font-size: 14px;
            font-weight: 700;
            cursor: pointer;
            border: none;
            transition: 0.2s;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            white-space: nowrap;

            box-shadow: 0 0 0 0px transparent !important;

            border: 1px solid #d1d5db;
            color: #4b5563;
          }

          #AICopywriterSelectModal .ai-copywriter-select-create:hover {
            border-color: var(--cw-mint) !important;
            background: #f0fdfe !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-actions {
            display: flex !important;
            align-items: center !important;
            gap: 8px !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-button {
            height: 46px !important;
            margin: 0 !important;
            padding: 0 16px !important;
            border-radius: 5px !important;
            font-size: 14px !important;
            font-weight: 700 !important;
            line-height: 44px !important;
            cursor: pointer !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-button.cancel {
            border: 1px solid #d3dae1 !important;
            background: #fff !important;
            color: #3f4a57 !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-button.apply {
            border: 1px solid var(--cw-mint) !important;
            background: var(--cw-mint) !important;
            color: #0A1A2F !important;
          }

          #AICopywriterSelectModal .ai-copywriter-select-button.apply:disabled {
            border-color: #dbe2e8 !important;
            background: #dbe2e8 !important;
            color: #fff !important;
            cursor: not-allowed !important;
          }

          @media (max-width: 660px) {
            #AICopywriterSelectModal .ai-copywriter-select-modal {
              width: calc(100vw - 12px) !important;
              height: calc(100vh - 12px) !important;
            }

            #AICopywriterSelectModal .ai-copywriter-select-item-meta {
              flex-wrap: wrap;
            }

            #AICopywriterSelectModal .ai-copywriter-select-footer {
              align-items: stretch !important;
              flex-direction: column !important;
            }

            #AICopywriterSelectModal .ai-copywriter-select-create,
            #AICopywriterSelectModal .ai-copywriter-select-actions,
            #AICopywriterSelectModal .ai-copywriter-select-button {
              width: 100% !important;
              min-width: 0 !important;
            }
          }
        `}</style>

        <div
          id="AICopywriterSelectModal"
          className="ai-copywriter-select-overlay"
        >
          <section
            ref={this.modalRef}
            className="ai-copywriter-select-modal"
            role="dialog"
            aria-modal="false"
            aria-labelledby="copywriterSelectTitle"
            style={{
              transform: `translate(calc(-50% + ${dragX}px), calc(-50% + ${dragY}px))`,
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div
              className="ai-copywriter-select-header"
              onMouseDown={this.startModalDrag}
              title="헤더를 드래그하여 팝업을 이동할 수 있습니다."
            >
              <div className="ai-copywriter-select-heading">
                <h2 id="copywriterSelectTitle" className="ai-copywriter-select-title">
                  AI 카피라이터 선택
                </h2>
                <p className="ai-copywriter-select-description">
                  저장해둔 AI 카피라이터 설정입니다. 선택한 설정의 브랜드 정보, 저장 방법, 참고글, 말투 기준이 문구 생성에 반영됩니다.
                </p>
              </div>

              <button
                type="button"
                className="ai-copywriter-select-close"
                aria-label="팝업 닫기"
                onClick={this.handleClose}
              >
                ×
              </button>
            </div>

            <div className="ai-copywriter-select-body">
              <div className="ai-copywriter-select-search-wrap">
                <i className="fa fa-search ai-copywriter-select-search-icon" aria-hidden="true" />
                <input
                  ref={this.searchRef}
                  type="search"
                  className="ai-copywriter-select-search"
                  placeholder="설정 제목으로 검색"
                  value={searchQuery}
                  onChange={(e) => this.setState({ searchQuery: e.target.value })}
                />
              </div>

              <div className="ai-copywriter-select-list">
                {isLoading ? (
                  <div className="ai-copywriter-select-empty">AI 카피라이터를 불러오는 중입니다.</div>
                ) : filteredPresets.length > 0 ? (
                  filteredPresets.map(this.renderPresetItem)
                ) : (
                  <div className="ai-copywriter-select-empty">
                    {normalizedQuery ? '검색 결과가 없습니다.' : '저장된 AI 카피라이터가 없습니다.'}
                  </div>
                )}
              </div>
            </div>

            <div className="ai-copywriter-select-footer">
              <button
                type="button"
                className="ai-copywriter-select-create"
                disabled={isApplyingDefault || !!presetEditorRequest}
                onClick={() => this.openPresetEditor()}
              >
                + AI 카피라이터 추가
              </button>

              <div className="ai-copywriter-select-actions">
                <button
                  type="button"
                  className="ai-copywriter-select-button apply"
                  disabled={!canApply}
                  onClick={this.handleApply}
                >
                  {isApplyingDefault ? '적용 중...' : '적용'}
                </button>
              </div>
            </div>
          </section>
        </div>
        {presetEditorRequest && PresetEditor && (
          <PresetEditor
            key={presetEditorRequest.presetId ?? 'new'}
            isPresetModal
            editPresetId={presetEditorRequest.presetId}
            onCancelEdit={this.handleCancelPresetEditor}
            onPresetSaved={this.handlePresetSaved}
          />
        )}
      </>
    );
  }
}

export default AICopywriterModal;
